<template>
  <v-container>
    <v-form v-model="valid" ref="form">
      <div id="form-scroll-container">
        <v-row dense class="mb-4">
          <v-col cols="12" class="d-flex justify-end">
            <v-btn color="orange darken-2" style="color: #fff" @click="uploadModalOpen = true" class="py-5">
              <v-icon left>fas fa-file-excel</v-icon>
              {{ $t("views.trips.create_trip.upload_excel") }}
            </v-btn>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="6">
            <v-card outlined class="pa-4 rounded-lg">
              <v-row dense>
                <v-col cols="12">
                  <v-text-field v-model="formData.number_travel_main" :label="$t('views.trips.create_trip.trip_number')"
                    outlined dense :rules="[rules.required]" :error="!formData.number_travel_main && formTouched"
                    persistent-hint />
                </v-col>
                <v-col cols="6">
                  <v-autocomplete v-model="formData.type_operation" :items="typeOperations"
                    :label="$t('views.trips.create_trip.description_type_operation')" outlined dense clearable
                    :rules="[rules.required]" :error="!formData.code_type_operation && formTouched" persistent-hint
                    :search-input.sync="searchTypeOperation"
                    :loading="loadingScroll.typeOperation || isSearchingTypeOperation"
                    :data-scroll-id="'typeOperation'" @focus="setActiveScroll('typeOperation')"
                    @change="handleOperationTypeCode" />
                </v-col>
                <v-col cols="6">
                  <v-text-field v-model="formData.code_type_operation"
                    :label="$t('views.trips.create_trip.code_type_operation')" outlined dense
                    :readonly="operationTypeCodeExists" :rules="[rules.required]"
                    :error="!formData.code_type_operation && formTouched" persistent-hint />
                </v-col>
                <v-col cols="12">
                  <v-autocomplete v-model="selectedTripType" :items="tripTypeOptions" item-text="name"
                    item-value="idTypeTrip" :label="$t('views.trips.create_trip.trip_type')" outlined dense clearable
                    :rules="[rules.required]" :error="!formData.code_type_trip && formTouched" persistent-hint
                    @change="handleTripTypeSelected" />
                </v-col>
                <v-col cols="12">
                  <v-text-field v-model.number="formData.weight" :label="$t('views.trips.create_trip.weight')" outlined
                    dense type="number" />
                </v-col>
                <v-col cols="12">
                  <v-text-field v-model="formData.volume" :label="$t('views.trips.create_trip.volume')" outlined dense
                    inputmode="numeric" maxlength="15" counter="15" :rules="[rules.volume]"
                    @input="handleVolumeInput" />
                </v-col>
                <v-col cols="12">
                  <v-text-field v-model="formData.prices_freight" :label="$t('views.trips.create_trip.freight_price')"
                    outlined dense type="number" />
                </v-col>
                <v-col cols="12">
                  <v-autocomplete v-model="formData.code_currency_freight" :items="currencyTypes"
                    :label="$t('views.trips.create_trip.currency_type')" outlined dense clearable />
                </v-col>
                <v-col cols="12">
                  <v-autocomplete v-model="formData.id_method_freight_payment" :items="freightPaymentMethods"
                    item-text="name" item-value="id"
                    :label="$t('views.trips.create_trip.freight_payment_method')" outlined dense clearable
                    :loading="loadingFreightPaymentMethods" @change="handleFreightPaymentMethodSelected" />
                </v-col>
                <!-- Transport Company -->
                <!-- <v-col cols="12">
                  <v-autocomplete v-model="formData.code_company" :items="transportCompanies"
                    :label="$t('views.trips.create_trip.transport_company')" outlined dense clearable
                    :data-scroll-id="'transportCompanies'" @focus="setActiveScroll('transportCompanies')" />
                </v-col> -->
              </v-row>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <v-card outlined class="pa-4 rounded-lg">
              <v-row dense>
                <v-col cols="12">
                  <h4 class="text-subtitle-1 font-weight-bold mb-2">
                    <v-icon class="mr-2" style="color: #1D1B59;">fas fa-route</v-icon>
                    {{ $t("views.trips.create_trip.stop") }}
                  </h4>
                  <v-list v-if="formData.stops.length" dense>
                    <v-list-item v-for="(stops, index) in formData.stops" :key="index">
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ $t(`views.trips.create_trip.stop`) }}: {{ stops.type_location }}
                          {{ stops.description_company_client }} - {{ stops.code_location_destiny }} ({{
                            stops.description_location_destiny }})
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ $t('views.trips.create_trip.scheduled_date') }}: {{ stops.datetime_in_place }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                  <v-alert v-else type="info" class="mt-2">
                    {{ $t("views.trips.create_trip.no_stops_added") }}
                  </v-alert>
                </v-col>
                <v-col cols="12">
                  <v-btn block color="orange darken-2" text-capitalize @click="modalVisible = true"
                    style="color: #ffffff" class="mb-4 py-5">
                    <v-icon left class="mr-2" style="font-size: 20px;">fas fa-truck</v-icon>
                    {{ $t("views.trips.create_trip.configure_stop") }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>

        <v-row dense class="my-6">
          <v-col cols="12">
            <h3 class="title-section">
              {{ $t("views.trips.create_trip.stop_details") }}
            </h3>
            <v-divider class="mb-4" />
          </v-col>
          <v-col cols="12">
            <v-card outlined class="pa-4 rounded-lg">
              <v-row align="center" dense>
                <v-col cols="12" md="12" gap-4 class="d-flex justify-end align-center">
                  <v-btn color="orange darken-2" class="mr-2 py-5" text-capitalize @click="showObservationsModal = true"
                    style="color: #ffffff">
                    <v-icon left>fas fa-eye</v-icon>
                    {{ $t("views.trips.create_trip.trip_observations") }}
                  </v-btn>
                  <v-btn color="orange darken-2" class="mr-2 py-5" text-capitalize @click="showReferencesModal = true"
                    style="color: #ffffff">
                    <v-icon left>fas fa-clipboard</v-icon>
                    {{ $t("views.trips.create_trip.references") }}
                  </v-btn>
                </v-col>
              </v-row>
              <RouteSelector :stops="formData.stops" @update:selectedOption="handleSelectedOption"
                @update:route="updateRouteInForm" />
            </v-card>
          </v-col>
        </v-row>
        <v-row justify="end" class="mt-6">
          <v-btn color="grey" text @click="cancel" class="py-5">
            {{ $t("views.trips.create_trip.cancel") }}
          </v-btn>
          <v-btn color="#1D1B59" class="ml-4 py-5" @click="openReplicateModal" style="color: #ffffff"
            :disabled="!canUseReplicate">
            {{ $t("views.trips.create_trip.replicate") }}
          </v-btn>
          <v-btn color="#1D1B59" class="ml-4 py-5" @click="submit" style="color: #ffffff" :disabled="!valid">
            {{ $t("views.trips.create_trip.create_trip") }}
          </v-btn>
        </v-row>
      </div>
    </v-form>
    <transition name="fade">
      <v-alert v-if="showSuccessAlert" type="success" elevation="10" class="success-alert" border="left" colored-border>
        {{ $t("views.trips.create_trip.success_message") }}
      </v-alert>
    </transition>

    <AddStopModal :isOpen="modalVisible" @close="modalVisible = false" @add="handleStops" @update-location="addLocation"
      :businessUnits="businessUnits" :salesChannels="salesChannels" :productTypes="productTypes"
      :commodityTypes="commodityTypes" :idCompanyParent="idCompanyParent"
      :numberMainTravel="formData.number_travel_main" />
    <ReferencesModal :isOpen="showReferencesModal" :doc_ref1.sync="formData.doc_ref1" :doc_ref2.sync="formData.doc_ref2"
      @close="showReferencesModal = false" />
    <ObservationsModal :isOpen="showObservationsModal" :observations.sync="formData.observations"
      @close="showObservationsModal = false" />
    <ReplicateTripModal :isOpen="replicateModalVisible" @close="replicateModalVisible = false" :formData="formData"
      :idCompanyParent="idCompanyParent" />
    <UploadExcelModal :isOpen="uploadModalOpen" @close="uploadModalOpen = false" @offers-created="updateTripNumber" />

  </v-container>
</template>


<script>
import AddStopModal from "@/components/views/Trip/Create/Modals/AddModals/AddStopModal.vue";
import ReferencesModal from "@/components/views/Trip/Create/Modals/ReferencesModal.vue";
import ObservationsModal from "@/components/views/Trip/Create/Modals/ObservationsModal.vue";
import ReplicateTripModal from "@/components/views/Trip/Create/Modals/ReplicateTripModal.vue";
import { mapGetters } from "vuex";
import { getLastTripNumber } from "@/components/views/Settings/ConfigTripNumber/Services/useTripNumberConfig";
import RouteSelector from "@/components/views/Trip/Create/RouteSelector.vue";
import UploadExcelModal from "@/components/views/Trip/Create/components/UploadExcelModal.vue";
import { buildManualTripPayload } from "@/components/views/Trip/Create/buildManualTripPayload";


export default {
  components: {
    AddStopModal,
    ReferencesModal,
    ObservationsModal,
    ReplicateTripModal,
    RouteSelector,
    UploadExcelModal
  },
  data() {
    return {
      uploadModalOpen: false,
      selectedRouteOption: "",
      valid: false,
      canReplicate: true,
      originalTripNumber: "",
      formTouched: false,
      modalVisible: false,
      selectedTripType: null,
      showRouteMapModal: false,
      selectedRoute: null,
      showReferencesModal: false,
      showObservationsModal: false,
      replicateModalVisible: false,
      operationTypeCodeExists: true,
      showSuccessAlert: false,
      // OperationType
      typeOperationPage: 1,
      totalTypeOperationPages: null,
      // TransportiCompanies
      transportCompaniesPage: 1,
      totalTransportCompanies: null,
      activeScrollId: null,
      loadingScroll: {
        location: false,
        client: false,
        typeOperation: false,
        transportCompanies: false,
      },
      searchTypeOperation: "",
      isSearchingTypeOperation: false,
      loadingFreightPaymentMethods: false,
      typeOperationSearchTimeout: null,
      clients: [],
      locations: [],
      businessUnits: [],
      salesChannels: [],
      productTypes: [],
      commodityTypes: [],
      formData: {
        code_company: "",
        number_travel_main: "",
        number_travel_secondary: "",
        code_type_operation: "",
        type_operation: "",
        code_type_trip: "",
        id_route: "",
        name_route: "",
        weight: null,
        volume: "",
        prices_freight: "",
        code_currency_freight: "",
        id_method_freight_payment: null,
        method_freight_payment: "",
        observations: "",
        doc_ref1: "",
        doc_ref2: "",
        stops: []
      },
      operationTypeOptions: [],
      tripTypeOptions: [],
      transportCompaniesList: [],
      currencyTypes: ["COP", "USD", "EUR", "MXN"],
      freightPaymentMethods: [],
      rules: {
        required: (value) => !!value || this.$t("views.trips.create_trip.required"),
        volume: (value) => !value || /^\d{1,15}$/.test(value)
          || this.$t("views.trips.create_trip.invalid_volume"),
      },
    };
  },
  async mounted() {
    try {
      const config = await getLastTripNumber(this.axios);
      if (config && config.fullTripNumber) {
        this.formData.number_travel_main = config.fullTripNumber;
        this.originalTripNumber = config.fullTripNumber;
      } else {
        this.canReplicate = false;
      }

      await Promise.all([
        this.loadTypeOperations(),
        this.loadTransportCompanies(),
        this.loadTripTypes(),
        this.loadFreightPaymentMethods(),
      ]);

    } catch (error) {
      if (error.response && error.response.status === 204) {
        this.canReplicate = false;
      }
    }
  },
  computed: {
    canUseReplicate() {
      return (
        this.canReplicate &&
        this.formData.number_travel_main === this.originalTripNumber
      );
    },
    ...mapGetters({
      user: "auth/user",
    }),
    idCompanyParent() {
      let userMap = JSON.parse(this.user);
      return userMap.user.idCompany;
    },
    typeOperations() {
      return this.operationTypeOptions.map(c => c.description);
    },
    transportCompanies() {
      return this.transportCompaniesList.map(c => c.businessName);
    }
  },
  watch: {
    searchTypeOperation(value) {
      clearTimeout(this.typeOperationSearchTimeout);
      if (!value) {
        return;
      }

      this.typeOperationSearchTimeout = setTimeout(() => {
        this.loadTypeOperationPagesForSearch(value);
      }, 300);
    }
  },
  methods: {
    handleVolumeInput(value) {
      this.formData.volume = String(value || "").replace(/\D/g, "").slice(0, 15);
    },
    handleFreightPaymentMethodSelected(id) {
      const method = this.freightPaymentMethods.find(item => item.id === id);
      this.formData.method_freight_payment = method ? method.name : "";
    },
    async loadFreightPaymentMethods() {
      this.loadingFreightPaymentMethods = true;
      try {
        const response = await this.axios.get("/FreightPaymentMethod");
        this.freightPaymentMethods = response.data || [];
      } catch (error) {
        console.error("Error loading freight payment methods:", error);
        this.freightPaymentMethods = [];
      } finally {
        this.loadingFreightPaymentMethods = false;
      }
    },
    async updateTripNumber(newTripNumber) {
      if (newTripNumber) {
        this.formData.number_travel_main = newTripNumber;
        this.originalTripNumber = newTripNumber;
      } else {
        this.canReplicate = false;
      }
    },

    updateRouteInForm({ id_route, name_route }) {
      this.formData.id_route = id_route;
      this.formData.name_route = name_route;
    },

    handleSelectedOption(newValue) {
      this.selectedRouteOption = newValue;
    },
    handleTripTypeSelected(value) {
      this.formData.code_type_trip = String(value);
    },


    setActiveScroll(id) {
      this.activeScrollId = id;
      this.pollForDropdownScrollListener(id);
    },

    pollForDropdownScrollListener(id) {
      const interval = setInterval(() => {
        const menus = document.querySelectorAll('.v-menu__content');

        menus.forEach(menu => {
          const alreadyAttached = menu.dataset[`scrollAttached_${id}`];
          if (alreadyAttached) return;
          if (this.activeScrollId !== id) return;

          menu.dataset[`scrollAttached_${id}`] = 'true';
          menu.addEventListener('scroll', (e) => this.handleScroll(e, id));
        });
      }, 500);

      setTimeout(() => {
        clearInterval(interval);
      }, 5000);
    },

    handleScroll(e, id) {
      const { scrollTop, scrollHeight, clientHeight } = e.target;

      const preloadMargin = 50;

      if (scrollTop + clientHeight >= scrollHeight - preloadMargin) {
        if (this.loadingScroll[id] || (id === 'typeOperation' && this.isSearchingTypeOperation)) {
          return;
        }
        this.loadingScroll[id] = true;

        if (id === 'transportCompanies') {
          if (!this.totalTransportCompanies || this.transportCompaniesPage < this.totalTransportCompanies) {
            this.transportCompaniesPage++;
            this.loadTransportCompanies().finally(() => {
              this.loadingScroll[id] = false;
            });
          } else {
            this.loadingScroll[id] = false;
          }
        } if (id === 'typeOperation') {
          if (!this.totalTypeOperationPages || this.typeOperationPage < this.totalTypeOperationPages) {
            this.typeOperationPage++;
            this.loadTypeOperations().finally(() => {
              this.loadingScroll[id] = false;
            });
          } else {
            this.loadingScroll[id] = false;
          }
        }
      }
    },
    async loadTripTypes() {
      try {
        const res = await this.axios.get("/TypeTrip");
        const data = res.data || [];

        this.tripTypeOptions = data;
      } catch (error) {
        console.error("Error loading trip types:", error);
      }
    },
    async loadTypeOperations() {
      try {
        const size = 50;
        const res = await this.axios.get(`/TypeOperation/GetAsyncTypeOperationGET/${this.idCompanyParent}/${this.typeOperationPage}/${size}`);
        const data = res.data || [];
        if (data.length < size) this.totalTypeOperationPages = this.typeOperationPage;
        this.operationTypeOptions.push(...data);
      } catch (error) {
        console.error("Error loading typeOperations:", error);
      }
    },
    async loadTypeOperationPagesForSearch(searchValue) {
      if (!searchValue) {
        return;
      }

      if (this.isSearchingTypeOperation) {
        this.typeOperationSearchTimeout = setTimeout(() => {
          this.loadTypeOperationPagesForSearch(searchValue);
        }, 300);
        return;
      }

      this.isSearchingTypeOperation = true;
      const currentSearch = searchValue;

      try {
        while (
          this.searchTypeOperation === currentSearch &&
          (!this.totalTypeOperationPages || this.typeOperationPage < this.totalTypeOperationPages)
        ) {
          this.typeOperationPage++;
          await this.loadTypeOperations();
        }
      } finally {
        this.isSearchingTypeOperation = false;
      }
    },

    async loadTransportCompanies() {
      try {
        const size = 50;
        const res = await this.axios.get(`/TransportCompany/GetAsyncTransporCompaniesGET/${this.idCompanyParent}/${this.transportCompaniesPage}/${size}`);
        const data = res.data || [];
        if (data.length < size) this.totalTransportCompanies = this.transportCompaniesPage;
        this.transportCompaniesList.push(...data);
      } catch (error) {
        console.error("Error loading transportCompanies:", error);
      }
    },
    handleOperationTypeCode(value) {
      const match = this.operationTypeOptions.find(c => c.description === value);

      if (match) {
        this.formData.code_type_operation = match.id_operationType_WS;
        this.operationTypeCodeExists = true;
      } else {
        this.formData.code_type_operation = "";
        this.operationTypeCodeExists = true;
        this.formData.type_operation = "";
      }
    },

    addLocation(newLocation) {
      const alreadyExists = this.locations.some(
        loc => loc.destinationCode === newLocation.destinationCode
      );

      if (!alreadyExists) {
        this.locations.push(newLocation);
      }
    },
    cancel() {
      this.$refs.form.reset();
      this.formTouched = false;
    },
    handleStops(stops) {
      this.formData.stops = stops;
    },
    openReplicateModal() {
      this.replicateModalVisible = true;
    },
    async submit() {
      this.formTouched = true;

      if (!this.$refs.form.validate()) {
        console.warn("Invalid form", this.formData);
        return;
      }
      try {
        await this.axios.post("/Travels/CreateManualTrip", buildManualTripPayload(this.formData));
        this.showSuccessAlert = true;
        setTimeout(() => {
          this.showSuccessAlert = false;
        }, 2000);
        this.resetFormData();
        window.location.reload();
      } catch (error) {
        console.error("Error submitting form:", error);
      }
    },
    async resetFormData() {
      this.formData = {
        code_company: "",
        number_travel_main: "",
        number_travel_secondary: "",
        code_type_operation: "",
        type_operation: "",
        code_type_trip: "",
        id_route: "",
        name_route: "",
        weight: 0,
        volume: "",
        prices_freight: "",
        code_currency_freight: "",
        id_method_freight_payment: null,
        method_freight_payment: "",
        observations: "",
        doc_ref1: "",
        doc_ref2: "",
        stops: []
      };
      this.selectedTripType = null;
      this.formTouched = false;
      try {
        const config = await getLastTripNumber(this.axios);
        if (config && config.fullTripNumber) {
          this.formData.number_travel_main = config.fullTripNumber;
        }
      } catch (error) {
        console.error("Error al obtener nuevo número de viaje:", error);
      }
    }
  },
  beforeDestroy() {
    clearTimeout(this.typeOperationSearchTimeout);
  },
};
</script>
<style lang="scss" scoped src="./TripCreateForm.module.scss" />
